/**
 * Points -> per-player deltas, per the MCR 2006 rulebook (Serious and
 * Official modes) or the app's own Fun-mode convention layered on top (see
 * settings.ts).
 *
 * Structurally different from HKOS's payout.ts, not just a different
 * formula: MCR's Basic Points are additive (straight off the 81-pattern fan
 * table, no exponential base^fan), every win carries a flat "Extra Points"
 * component on top, and in Serious/Official mode a hand that doesn't reach
 * the minimum isn't just a non-event — the declarer owes a real penalty
 * ("False Hu"). Serious and Official share the exact same settlement code
 * path (`settleRealWin`/`settleFalseHu`), zero-sum by construction in both
 * branches — they differ only in WHICH values feed that math, resolved once
 * via `effectiveSeriousSettings` before either branch runs (Official always
 * gets the rulebook's own numbers, Serious gets whatever the settings say).
 * Fun mode instead invents the winner's gain from nowhere and never debits
 * anyone (see settings.ts's doc comment for why).
 *
 * As with HKOS, a settlement bug that fabricates or drops points is the
 * worst failure mode — it undermines trust in the leaderboard even though
 * no real money is ever involved — so the zero-sum invariant fails loudly
 * (throws) rather than silently, in every branch that's supposed to hold it.
 */

import type { DrawContext, Payment, PlayerId, ScoreResult, WinContext } from '../types';
import { effectiveSeriousSettings, type McrSeriousModeSettings, type McrSettings } from './settings';

export function settleWin(
  result: ScoreResult,
  ctx: WinContext,
  players: PlayerId[],
  settings: McrSettings,
): Payment[] {
  if (settings.mode === 'fun') {
    return result.meetsMinimum
      ? settleFunWin(result, ctx, players, settings)
      : noPayments(players); // below minimum: a no-op, like a draw — never a penalty in Fun mode
  }
  // Serious and Official share the same settlement math — Official just
  // forces the rulebook's own values via effectiveSeriousSettings, ignoring
  // whatever is actually stored in settings.serious.
  const serious = effectiveSeriousSettings(settings);
  return result.meetsMinimum
    ? settleRealWin(result, ctx, players, serious)
    : settleFalseHu(ctx, players, serious);
}

function settleRealWin(
  result: ScoreResult,
  ctx: WinContext,
  players: PlayerId[],
  serious: McrSeriousModeSettings,
): Payment[] {
  const deltas = new Map<PlayerId, number>(players.map((p) => [p, 0]));
  const basic = result.basepoints;
  const extra = serious.extraPoints;

  const pay = (payer: PlayerId, amount: number) => {
    deltas.set(payer, (deltas.get(payer) ?? 0) - amount);
    deltas.set(ctx.winner, (deltas.get(ctx.winner) ?? 0) + amount);
  };

  if (ctx.selfDraw) {
    // Every other player pays Basic + Extra — the winner nets 3x(Basic+Extra).
    for (const p of players) {
      if (p !== ctx.winner) pay(p, basic + extra);
    }
  } else {
    if (!ctx.discarder) {
      throw new Error('Discard win requires ctx.discarder');
    }
    // Discarder pays the full Basic + Extra; the other two pay Extra only.
    pay(ctx.discarder, basic + extra);
    for (const p of players) {
      if (p !== ctx.winner && p !== ctx.discarder) pay(p, extra);
    }
  }

  return toZeroSumPayments(deltas);
}

/**
 * "False Hu": a declared win that doesn't reach minimumPoints is not a legal
 * win at all — the declarer forfeits a flat penalty to EACH other player,
 * and nobody's hand is actually scored. ctx.winner here means "the player
 * who declared" rather than an actual winner. Serious/Official only — Fun
 * mode's own "no negative deltas, ever" invariant means it never applies
 * this penalty; see `settleWin` above.
 */
function settleFalseHu(ctx: WinContext, players: PlayerId[], serious: McrSeriousModeSettings): Payment[] {
  const deltas = new Map<PlayerId, number>(players.map((p) => [p, 0]));
  const penalty = serious.falseWinPenalty;

  for (const p of players) {
    if (p === ctx.winner) continue;
    deltas.set(p, (deltas.get(p) ?? 0) + penalty);
    deltas.set(ctx.winner, (deltas.get(ctx.winner) ?? 0) - penalty);
  }

  return toZeroSumPayments(deltas);
}

/**
 * Fun mode: computes the SAME total the winner would have collected under
 * Serious mode's own self-draw/discard formulas (using fun.extraPoints in
 * place of serious.extraPoints), applies fun.multiplier, then credits only
 * the winner with it — nobody else is debited, matching HKOS's own Fun
 * mode philosophy (CLAUDE.md rule 3: "only the round winner ever gains
 * points, nobody else loses any"). Still requires a discarder on a discard
 * win, for the same internal-consistency reason Serious mode does, even
 * though the discarder's identity doesn't affect the amount here.
 */
function settleFunWin(
  result: ScoreResult,
  ctx: WinContext,
  players: PlayerId[],
  settings: McrSettings,
): Payment[] {
  if (!ctx.selfDraw && !ctx.discarder) {
    throw new Error('Discard win requires ctx.discarder');
  }
  const basic = result.basepoints;
  const extra = settings.fun.extraPoints;
  // Same shape as Serious mode's two totals: self-draw is 3x(basic+extra)
  // (every opponent notionally pays their own full share); a discard win is
  // (basic+extra) + 2*extra (one full payer, two extra-only payers).
  const total = ctx.selfDraw ? 3 * (basic + extra) : basic + 3 * extra;
  const winnerGain = total * settings.fun.multiplier;

  return players.map((player) => ({
    player,
    delta: player === ctx.winner ? winnerGain : 0,
  }));
}

/**
 * The base rulebook doesn't specify any payment for an exhaustive draw (wall
 * depleted, nobody has a complete hand) — unlike HKOS's optional tenpai
 * payment, there's no MCR rule text to implement here, so a draw simply
 * moves no points in either mode. Revisit if a specific tournament ruleset
 * layers a tenpai/noten payment on top of the base rules.
 */
export function settleDrawRound(_ctx: DrawContext, players: PlayerId[]): Payment[] {
  return noPayments(players);
}

function noPayments(players: PlayerId[]): Payment[] {
  return players.map((player) => ({ player, delta: 0 }));
}

function toZeroSumPayments(deltas: Map<PlayerId, number>): Payment[] {
  const payments = [...deltas].map(([player, delta]) => ({ player, delta }));
  const sum = payments.reduce((a, p) => a + p.delta, 0);
  if (sum !== 0) {
    throw new Error(
      `Settlement does not balance (sum=${sum}). This is a scoring bug: ${JSON.stringify(payments)}`,
    );
  }
  return payments;
}
